import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import ConfirmDialog from "@/components/ConfirmDialog";
import RupiahInput from "@/components/RupiahInput";
import DateField from "@/components/DateField";
import NotePresetInput from "@/components/NotePresetInput";
import LedgerEntriesTable from "@/components/income-ledger/LedgerEntriesTable";
import LedgerSummaryCards from "@/components/income-ledger/LedgerSummaryCards";
import LedgerPreviewPanel from "@/components/income-ledger/LedgerPreviewPanel";
import BillingInputPanel from "@/components/BillingInputPanel";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { todayJKT } from "@/lib/tz";

const toDateInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return ""; }
};

/**
 * Income ledger dialog — BOD & FAT. Each entry has date, amount, account, bank,
 * optional note. Account+bank are chosen from the shared Company Bank Account list
 * (managed under Settings → Company Bank Accounts).
 */
export default function IncomeLedgerDialog({ project, open, onOpenChange, onChanged, readOnly = false, initialMode = null }) {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: "", invoice_date: "", follow_up_date: "", amount: "", account_id: "", note: "", input_mode: "amount", billing_pct: "", net_received: "", pph_pct: 0, is_retention_claim: false });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {id, date, amount, account_id, note}
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null });
  // Combined-mode aggregate — when parent's vo_aggregation="combined", we merge entries
  // & contract from all children into a single ledger view. Read-only for aggregated
  // entries (user must go to source project to edit).
  const [combined, setCombined] = useState(null);
  // 149-bd: hasil dari BillingInputPanel — dipakai untuk submit + preview
  const [billingResult, setBillingResult] = useState(null);
  // In combined mode, user can pick which component (parent NP or specific VO/WO) the
  // income entry belongs to. Each component may have different retention_pct/retention_type,
  // so the Billing Input, retention factor, and target endpoint switch dynamically.
  // User request 09-Feb-2026: "Buatkan beberapa billing input, sesuai dengan jumlah main
  // contract dan jumlah VO. karena, bisa jadi main contract dipotong retensi, sedangkan
  // VO tidak dipotong retensi. atau berlaku sebaliknya sesuai pilihan pengaturan".
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  // Per-component amount inputs (combined mode). Keyed by component id.
  const [compAmounts, setCompAmounts] = useState({});
  // Per-component notes (combined mode). Setiap card punya note sendiri agar tiap
  // entry di ledger tercatat dengan catatan berbeda. Fallback ke form.note global.
  const [compNotes, setCompNotes] = useState({});
  const isCombinedParent = project?.project_type === "new_project" && project?.vo_aggregation === "combined";

  // Per-component retention map: { [componentId]: { held, claimed } }.
  // Spec 2026-02-10: OUTSIDE retention → held=0 per entry (retensi tidak dihitung
  // per entry, hanya muncul di retention claim). Hanya INSIDE yang akumulasi held.
  const compRetentionMap = useMemo(() => {
    const map = {};
    if (!combined?.is_combined) return map;
    for (const e of (combined.income_entries || [])) {
      const src = e._source_project_id;
      if (!src) continue;
      if (!map[src]) map[src] = { held: 0, claimed: 0 };
      if (e.is_retention_claim) {
        map[src].claimed += Number(e.amount) || 0;
      } else {
        const et = e.retention_type || "inside";
        if (et !== "outside") {
          map[src].held += Number(e.retention_amount) || 0;
        }
      }
    }
    return map;
  }, [combined]);

  useEffect(() => {
    if (open && project) {
      setEntries(project.income_entries || []);
      // 149-bk: initialMode dari AccurateUpdateMenu — "invoice" set waiting_payment=true.
      // 149-cw-r113: "payment" tidak lagi dipakai di sini (redirect ke PaymentReceiptDialog).
      // Legacy caller (initialMode=null) default false. Bila initialMode="invoice" waiting=true.
      const waiting = initialMode === "invoice";
      setForm({ date: waiting ? "" : todayJKT(), invoice_date: todayJKT(), amount: "", account_id: "", note: "", input_mode: "amount", billing_pct: "", net_received: "", pph_pct: 0, is_retention_claim: false, waiting_payment: waiting });
      api.get("/company-bank-accounts").then(({ data }) => setAccounts(data)).catch(() => setAccounts([]));
      // Load combined summary if this is a combined parent OR a child of a combined parent
      if (project.project_type !== "internal") {
        api.get(`/active-projects/${project.id}/combined-summary`)
          .then(({ data }) => {
            const isC = data && data.is_combined;
            setCombined(isC ? data : null);
            if (isC) {
              setEntries(data.income_entries || []);
              // Default selection: parent NP (head_id). Fallback to first component.
              setSelectedComponentId(data.head_id || (data.components?.[0]?.id ?? null));
            } else {
              setSelectedComponentId(null);
            }
          })
          .catch(() => { setCombined(null); setSelectedComponentId(null); });
      } else {
        setCombined(null);
        setSelectedComponentId(null);
      }
    }
  }, [open, project, initialMode]);

  // Selected component (only in combined mode). Retention config comes from HERE
  // when combined, else from the project itself.
  const selectedComp = combined?.is_combined
    ? (combined.components || []).find((c) => c.id === selectedComponentId) || combined.components?.[0] || null
    : null;
  const retPct = Number((selectedComp ?? project)?.retention_pct) || 0;
  const retType = (selectedComp ?? project)?.retention_type || "inside";
  // Contract value: for combined-mode parent (or its child), use aggregated contract.
  const contractValue = combined?.is_combined
    ? Number(combined.contract_value) || 0
    : Number(project?.contract_value) || 0;
  // Billing limit for a component: use that component's contract_value (bukan gabungan)
  // supaya over-billing per komponen dicek. Fallback ke aggregated bila belum ada component.
  const componentContract = selectedComp ? Number(selectedComp.contract_value) || 0 : contractValue;
  const pphPct = Number(form.pph_pct) || 0;

  // Retention formula (spec 2026-02-15 session 149-ae — combined VO drive formula):
  //   - combined VO           : retensi = amount × r/(100-r) (gross-up), untuk received invoice
  //                             net = amount − pph − retention
  //   - separate VO + outside : retensi = 0 (di-claim terpisah via Retention Claim)
  //                             net = amount − pph
  //   - separate VO + inside  : retensi = amount × r/100
  //                             net = amount − pph − retention
  const voAgg = combined?.is_combined
    ? "combined"
    : (project?.vo_aggregation || "separate");
  const retFactor = (retPct > 0 && retPct < 100)
    ? (voAgg === "combined"
        ? retPct / (100 - retPct)
        : (retType === "outside" ? 0 : retPct / 100))
    : 0;
  const pphFactor = pphPct / 100;
  // Net Received:
  // - "inside": Net = amount − PPh − retensi (retensi dikurangkan dari cash flow)
  // - "outside": Net = amount − PPh (retensi TIDAK dikurangkan)
  const netFactor = retType === "outside" ? (1 - pphFactor) : (1 - pphFactor - retFactor);

  // Derive invoice amount depending on input mode
  let derivedInvoice = null;
  if (form.input_mode === "percent" && form.billing_pct !== "" && contractValue > 0) {
    derivedInvoice = contractValue * (parseFloat(form.billing_pct) || 0) / 100;
  } else if (form.input_mode === "net" && form.net_received !== "") {
    const nr = parseFloat(form.net_received) || 0;
    derivedInvoice = netFactor > 0 ? nr / netFactor : 0;
  }
  const effectiveAmount = derivedInvoice != null ? derivedInvoice : parseFloat(form.amount) || 0;

  // If any retention claim already exists, project is considered "done — awaiting money".
  // Regular billing is blocked (backend also enforces this) and the form is forced into
  // Retention Claim mode until all remaining retention has been claimed.
  const hasClaim = useMemo(() => entries.some((e) => e.is_retention_claim), [entries]);

  // Auto-flip the form to claim-mode whenever the project transitions into hasClaim.
  useEffect(() => {
    if (hasClaim && !form.is_retention_claim) {
      setForm((prev) => ({ ...prev, is_retention_claim: true, input_mode: "amount", billing_pct: "", net_received: "", amount: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasClaim]);

  // When Retention Claim mode is active, auto-fill amount with total available retention.
  // Backend enforces amount = total_retention on save (only 1 claim allowed per project),
  // so UI must reflect the same rule: input is auto-populated & non-editable.
  // (User request 09-Feb-2026): "BILLING INPUT pada retention claim masih bisa diinput manual.
  // seharusnya angkanya otomatis sesuai nilai total retention dan tidak bisa di edit"
  // Retention available untuk claim.
  //   - inside : sum(retention_amount stored) − sum(claimed)
  //   - outside: contract × r/100 − sum(claimed) (retensi baru muncul saat claim)
  const retentionAvailableRaw = useMemo(() => {
    let cl = 0;
    for (const e of entries) if (e.is_retention_claim) cl += Number(e.amount) || 0;
    if (retType === "outside") {
      const cvBase = combined?.is_combined
        ? (combined.components || []).reduce((s, c) => {
            const rp = Number(c.retention_pct) || 0;
            const rt = c.retention_type || "inside";
            const cv = Number(c.contract_value) || 0;
            if (rt === "outside" && rp > 0 && rp < 100) return s + cv * rp / 100;
            return s;
          }, 0)
        : (contractValue * (retPct > 0 && retPct < 100 ? retPct / 100 : 0));
      return Math.max(0, cvBase - cl);
    }
    let ret = 0;
    for (const e of entries) {
      if (e.is_retention_claim) continue;
      const et = e.retention_type || retType;
      if (et === "outside") continue; // guard untuk entry lama
      ret += e.retention_amount != null ? Number(e.retention_amount) : 0;
    }
    return Math.max(0, ret - cl);
  }, [entries, retType, retPct, contractValue, combined]);
  useEffect(() => {
    // Auto-fill saat masuk claim mode:
    //   - inside : fixed = total retensi tertahan (readonly)
    //   - outside: prefill = max (contract × r/100), tapi user BOLEH edit manual (≤ max)
    if (form.is_retention_claim && retentionAvailableRaw > 0) {
      const target = String(retentionAvailableRaw);
      const inside = retType !== "outside";
      // Inside: paksa selalu = target. Outside: prefill sekali saja (biar user bisa edit).
      if (inside) {
        if (form.amount !== target || form.input_mode !== "amount") {
          setForm((prev) => ({ ...prev, input_mode: "amount", billing_pct: "", net_received: "", amount: target }));
        }
      } else if (!form.amount || form.input_mode !== "amount") {
        setForm((prev) => ({ ...prev, input_mode: "amount", billing_pct: "", net_received: "", amount: target }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.is_retention_claim, retentionAvailableRaw]);

  const totals = useMemo(() => {
    const isExempt = !!project?.is_ppn_exempt;
    let gross = 0, retention = 0, pph = 0, net = 0, claimed = 0;
    // Formula RETENSI DITAHAN (2026-02-15 fix, per request BOD):
    //   retensi_total = retention_pct × sum(DPP ditagihkan, exclude claim)
    // Sebelumnya pakai sum(retention_amount stored) yang untuk mode INSIDE
    // formula-nya `DPP × r/(100-r)` — bikin nilai retensi > ekspektasi. Sekarang
    // seragam simple `DPP × r/100` untuk INSIDE maupun OUTSIDE display.
    const projRetPct = Number(project?.retention_pct) || 0;
    for (const e of entries) {
      const a = Number(e.amount) || 0;
      const bruto = isExempt ? a : Math.round(a * 1.11);
      const ePct = e.retention_pct != null ? Number(e.retention_pct) : projRetPct;
      const r = e.is_retention_claim ? 0 : Math.round(a * ePct / 100);
      const p = e.pph_amount != null ? Number(e.pph_amount) : 0;
      const et = e.retention_type || retType;
      if (e.is_retention_claim) {
        claimed += a;
        net += bruto - p;
      } else {
        gross += a;
        pph += p;
        net += (et === "outside") ? (bruto - p) : (bruto - p - r);
        // Retensi per mode:
        //  - inside/outside: sama-sama retention_pct × amount (per BOD request 2026-02-15).
        retention += r;
      }
    }
    // Fix 2026-02-15: retention seragam pakai formula `retention_pct × sum(amount)`
    // untuk inside MAUPUN outside. Sebelumnya outside di-override `contract × r/100`
    // yang bikin retensi ditahan display = 10% dari kontrak PENUH walau tagihan
    // belum penuh. Sekarang retensi ditahan = pct × ditagih (proporsional actual).
    return { gross, retention, pph, net, claimed };
  }, [entries, retType, project?.is_ppn_exempt, project?.retention_pct]);

  // Billing cap: untuk outside → contract × (100-r)/100 (sisa retensi ditagih terpisah).
  const billableCap = retType === "outside" && retPct > 0 && retPct < 100
    ? contractValue * (100 - retPct) / 100
    : contractValue;
  const billableRemaining = Math.max(0, billableCap - totals.gross);
  // retentionAvailable = max claim − sum claimed. Untuk outside pakai retentionAvailableRaw
  // (yang sudah hitung basis contract × r/100). Untuk inside pakai totals.retention − claimed.
  const retentionAvailable = retType === "outside"
    ? retentionAvailableRaw
    : Math.max(0, totals.retention - totals.claimed);
  const isClaim = !!form.is_retention_claim;

  const formAmt = effectiveAmount;
  const formPreview = formAmt > 0 ? {
    billed: formAmt,
    pph: Math.round(formAmt * pphPct / 100 * 100) / 100,
    retention: Math.round(formAmt * retFactor * 100) / 100,
    net: Math.round(formAmt * netFactor * 100) / 100,
    pct_of_contract: contractValue > 0 ? (formAmt / contractValue) * 100 : null,
  } : null;

  const selectedAccount = accounts.find((a) => a.id === form.account_id);

  const add = async (e) => {
    e.preventDefault();
    if (!selectedAccount) { toast.error("Select a bank account"); return; }

    // Combined mode + regular billing: 2026-06-11 (user request) — semua card
    // yang diisi digabung menjadi SATU entry invoice di parent (head), karena
    // tipe project "VO Digabung". Rincian per komponen tetap terlacak lewat
    // `component_breakdown` + note gabungan.
    if (combined?.is_combined && !isClaim) {
      const comps = combined.components || [];
      const headId = combined.head_id || project.id;
      const toPost = comps.map((c) => {
        const raw = compAmounts[c.id];
        // RupiahInput sudah return digit string; tetap sanitize untuk safety.
        const amt = parseFloat(String(raw ?? "").replace(/[^0-9.]/g, "")) || 0;
        const used = c.total_income || 0;
        const remain = Math.max(0, (c.contract_value || 0) - used);
        return { c, amt, remain, over: amt > remain + 0.01 };
      }).filter((x) => x.amt > 0);
      if (toPost.length === 0) { toast.error("Isi minimal satu Billing Input"); return; }
      const overs = toPost.filter((x) => x.over);
      if (overs.length > 0) {
        toast.error(`Ada ${overs.length} komponen melebihi sisa kontrak — perbaiki dulu`);
        return;
      }
      // Susun breakdown + note gabungan. Format note per komponen:
      // "<Nama Komponen> Rp<nominal> — <subnote>". 1 komponen → subnote/global saja.
      const parts = toPost.map(({ c, amt }) => {
        const cNote = (compNotes[c.id] ?? "").trim() || form.note || "";
        return { c, amt, cNote };
      });
      const totalAmt = parts.reduce((s, x) => s + x.amt, 0);
      const breakdown = parts.map(({ c, amt, cNote }) => ({
        project_id: c.id,
        project_name: c.name,
        amount: amt,
        note: cNote || null,
      }));
      const combinedNote = parts.length === 1
        ? (parts[0].cNote || "")
        : parts.map(({ c, amt, cNote }) => `${c.name} ${formatIDR(amt)}${cNote ? ` — ${cNote}` : ""}`).join("; ");
      setBusy(true);
      try {
        // R4 (149-cw-r122): idempotency-key mengacu ke head + total gabungan.
        const idemKey = `il-combined-${headId}-${form.invoice_date || "no-inv-date"}-${totalAmt}-${combinedNote.slice(0, 20)}`;
        await api.post(`/active-projects/${headId}/income`, {
          date: form.waiting_payment ? null : new Date(form.date).toISOString(),
          invoice_date: form.invoice_date || null,
          amount: totalAmt,
          account_no: selectedAccount.account_no,
          bank_name: selectedAccount.bank_name,
          bank_account_id: selectedAccount.id,
          finance_source: "laporan_keuangan",
          idempotency_key: idemKey,
          note: combinedNote,
          pph_pct: pphPct,
          is_retention_claim: false,
          component_breakdown: breakdown,
        });
        // Reload aggregated summary setelah POST sukses.
        try {
          const { data: fresh } = await api.get(`/active-projects/${project.id}/combined-summary`);
          if (fresh?.is_combined) {
            setCombined(fresh);
            setEntries(fresh.income_entries || []);
          }
        } catch {}
        toast.success(`1 billing gabungan tercatat (${parts.length} komponen)`);
        setCompAmounts({});
        setCompNotes({});
        setForm({ ...form, note: "" });
        onChanged?.();
      } catch (err) {
        toast.error(formatDetail(err.response?.data?.detail));
      } finally { setBusy(false); }
      return;
    }

    // Single-form flow (non-combined atau retention claim mode)
    const amt = effectiveAmount;
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a positive amount or percentage"); return; }
    if (isClaim) {
      if (retentionAvailable <= 0) { toast.error("No retention available to claim"); return; }
      if (amt > retentionAvailable + 0.01) { toast.error(`Max retention claim: ${formatIDR(retentionAvailable)}`); return; }
    } else if (contractValue > 0 && amt > billableRemaining + 0.01) {
      toast.error(`Invoice exceeds remaining contract value (max ${formatIDR(billableRemaining)})`);
      return;
    }
    setBusy(true);
    try {
      // In combined mode + claim, submit to parent (claim hanya di parent).
      const targetPid = combined?.is_combined ? (combined.head_id || project.id) : project.id;
      // R4 (149-cw-r122): tandai asal + idempotency-key untuk cegah retry dobel.
      const idemKey = `il-${targetPid}-${form.invoice_date || "no-inv-date"}-${amt}-${isClaim ? "claim" : "reg"}-${(form.note || "").slice(0, 20)}`;
      const { data } = await api.post(`/active-projects/${targetPid}/income`, {
        date: form.waiting_payment ? null : new Date(form.date).toISOString(),
        invoice_date: form.invoice_date || null,
        follow_up_date: form.follow_up_date || null,
        amount: amt,
        account_no: selectedAccount.account_no,
        bank_name: selectedAccount.bank_name,
        bank_account_id: selectedAccount.id,
        finance_source: "laporan_keuangan",
        idempotency_key: idemKey,
        note: form.note,
        pph_pct: pphPct,
        is_retention_claim: isClaim,
        // 2026-10-19 (user request anti double-counting): tautan invoice sumber
        // retensi — WAJIB untuk klaim baru; backend menolak atau menandai
        // "Perlu Verifikasi" bila kosong/invalid.
        retention_source_entry_id: isClaim ? (form.retention_source_entry_id || null) : null,
        // 149-be: Billing engine snapshot + input tracking (backend simpan sbg audit trail)
        ...(billingResult ? {
          billing_input_method: billingResult.method,
          source_field: billingResult.source_field,
          dpp_amount: billingResult.dpp_amount,
          vat_rate: billingResult.vat_rate,
          vat_amount: billingResult.vat_amount,
          gross_invoice_amount: billingResult.gross_invoice_amount,
          income_tax_rate: billingResult.income_tax_rate,
          income_tax_base: billingResult.income_tax_base,
          income_tax_amount: billingResult.income_tax_amount,
          retention_rate: billingResult.retention_rate,
          retention_base: billingResult.retention_base,
          retention_amount: billingResult.retention_amount,
          net_received_amount: billingResult.net_received_amount,
          calculation_version: billingResult.calculation_version,
        } : {}),
        // Snapshot kontrak WAJIB dikirim tiap submit (per 149-be) supaya
        // record tetap valid meski kontrak kemudian di-update.
        contract_dpp_snapshot: contractValue,
        contract_gross_snapshot: project?.is_ppn_exempt ? contractValue : Math.round(contractValue * 1.11),
      });
      if (combined?.is_combined) {
        try {
          const { data: fresh } = await api.get(`/active-projects/${project.id}/combined-summary`);
          if (fresh?.is_combined) {
            setCombined(fresh);
            setEntries(fresh.income_entries || []);
          } else {
            setEntries((es) => [...es, data]);
          }
        } catch { setEntries((es) => [...es, data]); }
      } else {
        setEntries((es) => [...es, data]);
      }
      setForm({ ...form, amount: "", billing_pct: "", net_received: "", note: "" });
      toast.success(isClaim ? "Retention claim recorded" : "Income recorded");
      onChanged?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (eid) => {
    try {
      await api.delete(`/active-projects/${project.id}/income/${eid}`);
      setEntries((es) => es.filter((e) => e.id !== eid));
      toast.success("Income deleted");
      onChanged?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };
  const askDel = (eid) => setConfirmDel({ open: true, id: eid });

  const startEdit = (e) => {
    const matchAcc = accounts.find((a) => a.account_no === e.account_no && a.bank_name === e.bank_name);
    setEditing({
      id: e.id,
      date: toDateInput(e.date),
      invoice_date: toDateInput(e.invoice_date),
      amount: String(e.amount ?? ""),
      account_id: matchAcc?.id || "",
      manual_account_no: e.account_no || "",
      manual_bank_name: e.bank_name || "",
      note: e.note || "",
      // Waiting payment flag — kalau entry aslinya tidak punya date, itu artinya waiting.
      waiting_payment: !e.date,
      _source_project_id: e._source_project_id || project.id,
    });
  };
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    const amt = parseFloat(editing.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a positive amount"); return; }
    const acc = accounts.find((a) => a.id === editing.account_id);
    const account_no = acc?.account_no || editing.manual_account_no;
    const bank_name = acc?.bank_name || editing.manual_bank_name;
    if (!account_no || !bank_name) { toast.error("Bank account required"); return; }
    // Route ke source project (combined mode: entry milik VO tertentu, bukan parent).
    const targetPid = editing._source_project_id || project.id;
    try {
      const { data } = await api.patch(`/active-projects/${targetPid}/income/${editing.id}`, {
        date: editing.waiting_payment ? null : new Date(editing.date).toISOString(),
        invoice_date: editing.invoice_date || null,
        amount: amt,
        account_no,
        bank_name,
        note: editing.note,
      });
      setEntries((es) => es.map((e) => (e.id === editing.id ? data : e)));
      setEditing(null);
      toast.success("Income updated");
      onChanged?.();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="dialog-income">
        {readOnly && (
          <div className="border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 flex items-center gap-2" data-testid="income-readonly-hint">
            <span>⚠ <b>Read-only</b> — input & edit invoice sekarang di <b>Laporan Keuangan</b>. Dialog ini hanya untuk melihat detail entry.</span>
            <a href={`/finance-report?tab=invoice-piutang`} data-testid="income-open-finance-report"
              className="ml-auto underline font-semibold text-[#0B1D3A] hover:text-[#FF5A1F]">Buka Laporan Keuangan →</a>
          </div>
        )}
        <div className="text-[10px] text-slate-500 italic bg-slate-50 border border-slate-200 px-2 py-1" data-testid="income-official-note">
          Pembukuan resmi dilakukan di software akuntansi terpisah.
        </div>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Income Ledger · {project?.project_name}</DialogTitle>
          <DialogDescription>
            Record all billings. Retention <span className="font-semibold text-amber-700">{retPct}%</span> is auto-held per entry <span className="text-slate-500">— dikurangkan dari Received (Net) & dibayar lewat Retention Claim saat proyek selesai</span>.
          </DialogDescription>
        </DialogHeader>

        {/* 149-cw-r113: Tab strip dihapus — "Penerimaan Pembayaran Client" sekarang
            pakai PaymentReceiptDialog terpisah yang dibuka dari AccurateUpdateMenu.
            Dialog ini fokus untuk Invoice/Tagihan Baru + Retention Claim. */}

        <LedgerSummaryCards
          project={project}
          contractValue={contractValue}
          totals={totals}
          retPct={retPct}
        />


        <form onSubmit={add} className={`border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-6 gap-3 ${readOnly ? "hidden" : ""}`}>
          {/* Combined mode: SEMBUNYIKAN "Pilih Komponen Billing" — user request 09-Feb-2026.
              Multi-card billing inputs muncul di bawah shared fields (Bank, Date, PPh). */}
          {hasClaim && (
            <div className="md:col-span-6 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-[11px]" data-testid="income-claim-lock-banner">
              <div className="font-semibold text-amber-900 uppercase tracking-wider text-[10px]">Proyek Sudah Retention Claim</div>
              <div className="text-amber-800 mt-0.5">Retention claim sudah tercatat — <b>penambahan tagihan baru dinonaktifkan</b>. Proyek dianggap selesai, tinggal menunggu pembayaran retensi masuk. Anda hanya bisa menambah entry <b>Retention Claim</b> hingga semua retensi tertagih.</div>
            </div>
          )}
          <div className="md:col-span-6 flex items-center justify-between gap-3 flex-wrap border-b border-slate-100 pb-3">
            <label className={`flex items-center gap-2 text-xs select-none ${hasClaim ? "cursor-not-allowed opacity-90" : "cursor-pointer"}`} data-testid="income-claim-toggle">
              <input
                type="checkbox"
                checked={isClaim}
                disabled={hasClaim}
                onChange={(e) => setForm({ ...form, is_retention_claim: e.target.checked, input_mode: "amount", billing_pct: "", net_received: "", amount: "" })}
                className="h-4 w-4 accent-amber-600"
                data-testid="income-claim-checkbox"
              />
              <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-700">Retention Claim</span>
              <span className="text-[10px] text-slate-500">— {hasClaim ? "wajib (proyek sudah selesai)" : "tarik retensi yang sudah ditahan"}</span>
            </label>
            <div className="flex gap-3 text-[10px] tabular-nums">
              <div className="text-slate-500">
                <span className="overline block">Sisa Kontrak</span>
                <span className="font-semibold text-[#0B1D3A]">{formatIDR(billableRemaining)}</span>
              </div>
              <div className="text-slate-500">
                <span className="overline block">Retensi Tersedia</span>
                <span className="font-semibold text-amber-700">{formatIDR(retentionAvailable)}</span>
              </div>
            </div>
          </div>

          {/* 2026-10-19 (user request anti double-counting): tautan explicit
              ke invoice sumber retensi — WAJIB diisi saat membuat klaim baru;
              klaim lama tanpa tautan bisa dilengkapi FAT via Collection. */}
          {isClaim && (
            <div className="md:col-span-6 border border-amber-300 bg-amber-50 px-3 py-2 space-y-1.5" data-testid="claim-source-wrapper">
              <Label className="overline text-amber-800">Invoice Sumber Retensi (WAJIB) <span className="text-slate-500 italic normal-case text-[10px]">— pilih invoice reguler yang retensinya ditarik</span></Label>
              <Select value={form.retention_source_entry_id || ""} onValueChange={(v) => setForm({ ...form, retention_source_entry_id: v })}>
                <SelectTrigger data-testid="claim-source-select"><SelectValue placeholder="Pilih invoice sumber retensi…" /></SelectTrigger>
                <SelectContent>
                  {entries
                    .filter((e) => !e.is_retention_claim && !e.is_cancelled && (Number(e.retention_amount) || 0) > 0)
                    .map((e) => {
                      const held = Math.max(0, (Number(e.retention_amount) || 0) - (Number(e.retention_released_amount) || 0));
                      return (
                        <SelectItem key={e.id} value={e.id} data-testid={`claim-source-opt-${e.id}`}>
                          {`${e.invoice_number || e.invoice_no || e.id.slice(0, 8)} · ${fmtDate(e.invoice_date || e.date)} · Retensi tersisa ${formatIDR(held)}`}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              <p className="text-[9px] text-slate-600">Satu klaim hanya menguras satu invoice sumber. Bila invoice sumber ada di WO/VO lain, buka Collection → tombol <b>Perlu Verifikasi · Tautkan</b> pada baris klaim.</p>
            </div>
          )}

          <div className="md:col-span-2 space-y-1.5">
            <Label className="overline text-slate-600">Bank Account</Label>
            {/* 2026-10-09 (user request): rekening bank searchable. */}
            <SearchableSelect
              testid="income-account"
              value={form.account_id}
              onChange={(v) => setForm({ ...form, account_id: v })}
              options={accounts.map((a) => ({ value: a.id, label: `${a.bank_name} · ${a.account_no}` }))}
              placeholder={accounts.length === 0 ? "Add accounts in Settings" : "Choose…"}
              searchPlaceholder="Ketik nama bank / no rekening…"
              emptyText="Rekening tidak ditemukan"
              disabled={accounts.length === 0}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">Invoice Date</Label>
            <DateField testid="income-invoice-date" value={form.invoice_date} onChange={(v) => setForm({ ...form, invoice_date: v })} />
          </div>
          {/* 149-cw-r104: Follow-up reminder untuk client — muncul saat AR monitoring
              menandai invoice belum lunas & mendekati/tanggal follow_up_date. */}
          <div className="space-y-1.5">
            <Label className="overline text-slate-600">
              Tanggal Follow-Up (opsional) <span className="text-slate-400 italic normal-case text-[10px]">— kapan FAT harus reminder client</span>
            </Label>
            <DateField testid="income-follow-up-date" value={form.follow_up_date} onChange={(v) => setForm({ ...form, follow_up_date: v })} />
            <p className="text-[9px] text-slate-500">Ketik dd/mm/yyyy atau klik ikon kalender.</p>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label className="overline text-slate-600">Received Date</Label>
              {/* 149-cw-r113: Waiting toggle DISEMBUNYIKAN saat mode "invoice" (dari AccurateUpdateMenu).
                  Invoice baru selalu waiting. Received Date & Nominal Diterima diinput via
                  PaymentReceiptDialog (dari tombol "Terima Pembayaran" di AR Monitoring). */}
              {initialMode !== "invoice" && (
                <label className="flex items-center gap-1 text-[9px] cursor-pointer select-none" data-testid="income-waiting-toggle">
                  <input
                    type="checkbox"
                    checked={!!form.waiting_payment}
                    onChange={(e) => setForm({ ...form, waiting_payment: e.target.checked, date: e.target.checked ? "" : todayJKT() })}
                    className="h-3 w-3 accent-amber-600"
                    data-testid="income-waiting-checkbox"
                  />
                  <span className="uppercase tracking-wider font-semibold text-amber-700">Waiting Payment</span>
                </label>
              )}
            </div>
            {form.waiting_payment ? (
              <div className="h-9 border border-amber-300 bg-amber-50 px-3 flex items-center text-xs text-amber-800 italic" data-testid="income-date-waiting-badge">
                {initialMode === "invoice" ? "Invoice baru — pembayaran diinput terpisah" : "⏳ Uang belum masuk (Waiting Payment)"}
              </div>
            ) : (
              <DateField testid="income-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
            )}
            {initialMode !== "invoice" && (
              <p className="text-[9px] text-slate-500">Tanggal uang masuk, atau centang "Waiting Payment" jika belum diterima.</p>
            )}
          </div>

          {/* Input mode toggle — HIDDEN in combined mode (per-component cards di bawah handle input) */}
          {!combined?.is_combined && !isClaim && (
          <div className="md:col-span-6 space-y-1.5" data-testid="billing-input-wrapper">
            <BillingInputPanel
              vatRate={project?.is_ppn_exempt ? 0 : 0.11}
              incomeTaxRate={pphPct / 100}
              incomeTaxBase="dpp"
              retentionRate={retPct > 0 && retType !== "outside" ? retPct / 100 : 0}
              retentionBase="gross"
              contractDpp={contractValue}
              contractGross={project?.is_ppn_exempt ? contractValue : Math.round(contractValue * 1.11)}
              totalDppInvoicedPrev={totals.gross}
              totalGrossInvoicedPrev={project?.is_ppn_exempt ? totals.gross : Math.round(totals.gross * 1.11)}
              prevInvoiceTotal={entries.length > 0 ? Number(entries[entries.length - 1].amount) || 0 : 0}
              hideNetReceivedTab={initialMode === "invoice"}
              onResult={(r) => {
                setBillingResult(r);
                // Sync ke form.amount agar submit tetap kompatibel (amount = DPP saat ini).
                // Bawa juga engine fields untuk persistensi (149-be).
                setForm((prev) => ({
                  ...prev,
                  amount: String(r.dpp_amount || ""),
                  billing_pct: "", net_received: "", input_mode: "amount",
                  _engine: r,
                }));
              }}
              disabled={readOnly}
            />
          </div>
          )}
          {/* Legacy mode toggle & inputs — DISEMBUNYIKAN saat BillingInputPanel aktif (non-combined + non-claim) */}
          {!combined?.is_combined && isClaim && (
          <div className="md:col-span-3 space-y-1.5">
            <Label className="overline text-slate-600">Billing Input {isClaim && <span className="text-amber-700 normal-case ml-1">· Terkunci — nilai retensi otomatis</span>}</Label>
            <div className={`flex gap-0 border border-slate-300 mb-2 ${isClaim ? "opacity-50 pointer-events-none" : ""}`} data-testid="income-input-mode">
              <button
                type="button"
                data-testid="income-mode-amount"
                disabled={isClaim}
                onClick={() => setForm({ ...form, input_mode: "amount", billing_pct: "", net_received: "" })}
                className={`flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${form.input_mode === "amount" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-500"}`}
              >
                By Invoice
              </button>
              <button
                type="button"
                data-testid="income-mode-percent"
                disabled={contractValue <= 0 || isClaim}
                onClick={() => setForm({ ...form, input_mode: "percent", amount: "", net_received: "" })}
                className={`flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-40 ${form.input_mode === "percent" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-500"}`}
                title={isClaim ? "Retention claim mode — nominal dikunci ke total retensi" : (contractValue <= 0 ? "Set a contract value first" : "")}
              >
                By % Kontrak
              </button>
              <button
                type="button"
                data-testid="income-mode-net"
                disabled={isClaim}
                onClick={() => setForm({ ...form, input_mode: "net", amount: "", billing_pct: "" })}
                className={`flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold ${form.input_mode === "net" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-500"}`}
              >
                By Net Received
              </button>
            </div>
            {form.input_mode === "amount" && (() => {
              const isOutsideClaim = isClaim && retType === "outside";
              const claimLocked = isClaim && !isOutsideClaim; // inside claim = readonly
              return (
                <Input
                  data-testid="income-amount"
                  required
                  type="number"
                  min="0"
                  step="1"
                  value={form.amount}
                  readOnly={claimLocked}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={`rounded-none tabular-nums ${claimLocked ? "bg-amber-50 border-amber-300 text-amber-900 font-semibold cursor-not-allowed" : (isOutsideClaim ? "border-amber-300" : "")}`}
                  placeholder={isClaim ? (isOutsideClaim ? `Max ${formatIDR(retentionAvailable)} — bisa edit manual` : `Auto: ${formatIDR(retentionAvailable)}`) : "Invoice amount (before PPh & retention)"}
                  title={claimLocked ? "Nominal terkunci — auto-fill total retensi tertahan (Ditahan per penagihan)." : (isOutsideClaim ? `Max ${formatIDR(retentionAvailable)} (contract × retention %). Boleh diedit manual di bawah max.` : "")}
                />
              );
            })()}
            {isClaim && (
              <p className="text-[10px] text-amber-700 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 bg-amber-600 rounded-full" />
                {retType === "outside"
                  ? <>Max <b>{formatIDR(retentionAvailable)}</b> (kontrak × {retPct}%). Boleh diedit manual di bawah max. Retention claim hanya bisa 1× per proyek.</>
                  : <>Nominal auto-fill dari total retensi tertahan per penagihan. Retention claim hanya bisa 1× per proyek — nilai penuh.</>
                }
              </p>
            )}
            {form.input_mode === "percent" && !isClaim && (
              <div className="relative">
                <Input data-testid="income-billing-pct" required type="number" min="0" max="100" step="0.01" value={form.billing_pct} onChange={(e) => setForm({ ...form, billing_pct: e.target.value })} className="rounded-none tabular-nums pr-8" placeholder="e.g. 10" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs">%</span>
              </div>
            )}
            {form.input_mode === "net" && !isClaim && (
              <RupiahInput testid="income-net-received" value={form.net_received} onChange={(v) => setForm({ ...form, net_received: v })} placeholder="Actual money received (after PPh + retention)" />
            )}
          </div>
          )}

          {/* Combined-mode: multi-card billing inputs, satu per komponen.
              User request 09-Feb-2026: "buatkan beberapa kolom BILLING INPUT... sesuai
              jumlah VO yang ditambahkan". Setiap card punya amount + preview + submit
              sendiri, pakai retention config komponen tersebut. */}
          {combined?.is_combined && !isClaim && !readOnly && (
            <div className="md:col-span-6 space-y-2" data-testid="multi-billing-inputs">
              <div className="overline text-indigo-800 text-[10px] font-bold">Billing Input per Komponen</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(() => {
                  // Numbering VO/WO agar bisa dibedakan saat semua nama komponen sama (efek
                  // cascade rename). Main tetap tanpa nomor; VO/WO diberi urutan (VO-1, VO-2, …).
                  let voIdx = 0, woIdx = 0;
                  return (combined.components || []).map((c) => {
                    let compLabel = "";
                    if (c.project_type === "variation_order") { voIdx += 1; compLabel = `VO-${voIdx}`; }
                    else if (c.project_type === "working_order") { woIdx += 1; compLabel = `WO-${woIdx}`; }
                    return { ...c, _label: compLabel };
                  });
                })().map((c) => {
                  const cPhpPct = pphPct; // PPh shared
                  const cRetPct = Number(c.retention_pct) || 0;
                  const cRetType = c.retention_type || "inside";
                  // Spec 2026-02-10: outside → retention_amount = 0, cap = contract × (100-r)/100.
                  const cRetFactor = cRetType === "outside"
                    ? 0
                    : (cRetPct > 0 && cRetPct < 100 ? cRetPct / (100 - cRetPct) : 0);
                  const cPphFactor = cPhpPct / 100;
                  const cNetFactor = cRetType === "outside" ? (1 - cPphFactor) : (1 - cPphFactor - cRetFactor);
                  const cAmtRaw = compAmounts[c.id] || "";
                  const cAmt = parseFloat(String(cAmtRaw).replace(/[^0-9.]/g, "")) || 0;
                  const cUsedInc = c.total_income || 0;
                  // Sisa kontrak per komponen:
                  //   - inside : contract - billed - retensi ditahan (session 9)
                  //   - outside: contract × (100-r)/100 - billed (retensi ditagih terpisah)
                  const cRet = compRetentionMap[c.id] || { held: 0, claimed: 0 };
                  const cRetNet = Math.max(0, cRet.held - cRet.claimed);
                  const cCap = cRetType === "outside" && cRetPct > 0 && cRetPct < 100
                    ? (c.contract_value || 0) * (100 - cRetPct) / 100
                    : (c.contract_value || 0);
                  const cRemain = cRetType === "outside"
                    ? Math.max(0, cCap - cUsedInc)
                    : Math.max(0, cCap - cUsedInc - cRetNet);
                  const cOver = cAmt > cRemain + 0.01;
                  const cPreview = cAmt > 0 ? {
                    pph: Math.round(cAmt * cPphFactor * 100) / 100,
                    ret: Math.round(cAmt * cRetFactor * 100) / 100,
                    net: Math.round(cAmt * cNetFactor * 100) / 100,
                  } : null;
                  const isMain = c.project_type === "new_project";
                  return (
                    <div key={c.id} data-testid={`billing-card-${c.id}`} className={`border p-2.5 space-y-1.5 ${isMain ? "border-[#0B1D3A] bg-white" : "border-slate-200 bg-slate-50/40"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-[11px] text-[#0B1D3A] uppercase tracking-wider truncate">
                            {c.name}
                            {isMain && <span className="ml-1 text-[9px] px-1 py-0.5 bg-[#FF5A1F] text-white">MAIN</span>}
                            {!isMain && c._label && <span className="ml-1 text-[9px] px-1 py-0.5 bg-indigo-600 text-white">{c._label}</span>}
                          </div>
                          <div className="text-[9px] text-slate-500 tabular-nums" title={`Kontrak - Total Billed - Retensi Ditahan (netto)\nRp ${(c.contract_value || 0).toLocaleString('id-ID')} - Rp ${(c.total_income || 0).toLocaleString('id-ID')} - Rp ${cRetNet.toLocaleString('id-ID')}`}>
                            Kontrak {formatIDR(c.contract_value || 0)} · Sisa <span className={cRetNet > 0 ? "text-amber-700 font-semibold" : ""}>{formatIDR(cRemain)}</span>
                            {cRetNet > 0 && (
                              <span className="ml-1 text-amber-600" data-testid={`billing-card-retnet-${c.id}`}>· Ret ditahan {formatIDR(cRetNet)}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-[9px] px-1.5 py-0.5 border tabular-nums whitespace-nowrap font-semibold" style={{ borderColor: cRetPct > 0 ? (cRetType === "outside" ? "#c084fc" : "#f59e0b") : "#cbd5e1", color: cRetPct > 0 ? (cRetType === "outside" ? "#7e22ce" : "#b45309") : "#64748b", background: cRetPct > 0 ? (cRetType === "outside" ? "#faf5ff" : "#fffbeb") : "#f8fafc" }}>
                          {cRetPct > 0 ? `Ret ${cRetPct}% ${cRetType === "outside" ? "AKHIR" : "PER TAGIHAN"}` : "Tanpa Retensi"}
                        </div>
                      </div>
                      <RupiahInput
                        testid={`billing-amt-${c.id}`}
                        value={cAmtRaw}
                        onChange={(v) => setCompAmounts((s) => ({ ...s, [c.id]: v }))}
                        className={`w-full ${cOver ? "border-rose-400 bg-rose-50" : ""}`}
                        placeholder="Invoice amount (biarkan kosong bila komponen ini tidak ditagih)"
                      />
                      {/* Subnote per komponen — plain input tanpa preset (spec 2026-02-10).
                          Kosong → fallback ke Note global saat submit. */}
                      <Input
                        data-testid={`billing-note-${c.id}`}
                        value={compNotes[c.id] ?? ""}
                        onChange={(ev) => setCompNotes((s) => ({ ...s, [c.id]: ev.target.value }))}
                        placeholder="Subnote (Optional)"
                        className="rounded-none h-8 text-xs"
                      />
                      {cPreview && (
                        <div className="grid grid-cols-3 gap-1 text-[9px] tabular-nums">
                          <div className="bg-sky-50 border border-sky-200 px-1.5 py-1">
                            <div className="text-sky-700 uppercase tracking-wider text-[8px]">PPh</div>
                            <div className="font-semibold text-sky-800">{formatIDR(cPreview.pph)}</div>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 px-1.5 py-1">
                            <div className="text-amber-700 uppercase tracking-wider text-[8px]">Retensi</div>
                            <div className="font-semibold text-amber-800">{formatIDR(cPreview.ret)}</div>
                          </div>
                          <div className="bg-emerald-50 border border-emerald-200 px-1.5 py-1">
                            <div className="text-emerald-700 uppercase tracking-wider text-[8px]">Net</div>
                            <div className="font-semibold text-emerald-800">{formatIDR(cPreview.net)}</div>
                          </div>
                        </div>
                      )}
                      {cOver && (
                        <div className="text-[9px] text-rose-700 font-semibold">Melebihi sisa kontrak komponen ini</div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Grand-total preview: agregat semua card yang diisi. Memberi tahu user
                  berapa 1 billing gabungan yang akan tercatat sebelum klik Submit. */}
              {(() => {
                let gTotal = 0, gPph = 0, gRet = 0, gNet = 0, filled = 0;
                for (const c of (combined.components || [])) {
                  const raw = compAmounts[c.id];
                  const a = parseFloat(String(raw ?? "").replace(/[^0-9.]/g, "")) || 0;
                  if (a <= 0) continue;
                  filled += 1;
                  const rp = Number(c.retention_pct) || 0;
                  const rt = c.retention_type || "inside";
                  // Spec 2026-02-10: outside → ret=0 per entry.
                  const rf = rt === "outside"
                    ? 0
                    : (rp > 0 && rp < 100 ? rp / (100 - rp) : 0);
                  const pf = pphPct / 100;
                  const nf = rt === "outside" ? (1 - pf) : (1 - pf - rf);
                  gTotal += a;
                  gPph += Math.round(a * pf * 100) / 100;
                  gRet += Math.round(a * rf * 100) / 100;
                  gNet += Math.round(a * nf * 100) / 100;
                }
                if (filled === 0) return (
                  <p className="text-[10px] text-slate-500 italic">
                    Isi nominal di card manapun yang mau ditagih — kosongkan card lain. Semua akan disubmit sekaligus sebagai 1 billing gabungan.
                  </p>
                );
                return (
                  <div className="border-2 border-indigo-400 bg-indigo-50/50" data-testid="billing-grand-preview">
                    <div className="px-3 py-1.5 flex items-center justify-between border-b border-indigo-200 bg-indigo-100/60">
                      <span className="overline text-indigo-800 text-[10px] font-bold">Total Billing Gabungan ({filled} komponen)</span>
                    </div>
                    <div className="grid grid-cols-4">
                      <div className="px-3 py-2 border-r border-indigo-200">
                        <div className="overline text-slate-600 text-[9px]">Invoice</div>
                        <div className="tabular-nums font-semibold text-[#0B1D3A]" data-testid="grand-preview-billed">{formatIDR(gTotal)}</div>
                      </div>
                      <div className="px-3 py-2 border-r border-indigo-200 bg-sky-50/60">
                        <div className="overline text-sky-700 text-[9px]">Total Tax</div>
                        <div className="tabular-nums font-semibold text-sky-800" data-testid="grand-preview-pph">− {formatIDR(gPph)}</div>
                      </div>
                      <div className="px-3 py-2 border-r border-indigo-200 bg-amber-50/60">
                        <div className="overline text-amber-700 text-[9px]">Total Retensi</div>
                        <div className="tabular-nums font-semibold text-amber-800" data-testid="grand-preview-retention">− {formatIDR(gRet)}</div>
                      </div>
                      <div className="px-3 py-2 bg-emerald-50/60">
                        <div className="overline text-emerald-700 text-[9px]">Total Received (Net)</div>
                        <div className="tabular-nums font-semibold text-emerald-800" data-testid="grand-preview-net">{formatIDR(gNet)}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* PPh selector — available for both regular income and retention claims */}
          <div className="md:col-span-6 space-y-1.5">
            <Label className="overline text-slate-600">Income Tax (Potongan Pajak Penghasilan)</Label>
            <div className="inline-flex border border-slate-300 text-xs" data-testid="income-pph">
              {[
                { v: 0, label: "None" },
                { v: 1.75, label: "1.75%" },
                { v: 2.65, label: "2.65%" },
                { v: 4, label: "4%" },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  data-testid={`income-pph-${r.v}`}
                  onClick={() => setForm({ ...form, pph_pct: r.v })}
                  className={`px-4 py-1.5 uppercase tracking-wider font-semibold ${Number(form.pph_pct) === r.v ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-6 space-y-1.5">
            <Label className="overline text-slate-600">
              Note (optional){combined?.is_combined ? " · Global Fallback" : ""}
            </Label>
            <NotePresetInput
              testid="income-note"
              value={form.note}
              onChange={(v) => setForm({ ...form, note: v })}
              placeholder={combined?.is_combined ? "Dipakai bila card di atas tidak diisi note khusus" : "Ketik atau pilih preset"}
            />
          </div>

          <LedgerPreviewPanel
            formPreview={formPreview}
            isClaim={isClaim}
            formAmount={form.amount}
            pphPct={pphPct}
            retPct={retPct}
          />

          <div className="md:col-span-6 flex justify-end">
            <Button type="submit" data-testid="income-submit" disabled={busy} className={`rounded-none uppercase text-xs tracking-wider ${isClaim ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {busy ? "Saving…" : (isClaim ? "Claim Retention" : (combined?.is_combined ? "Submit Billing Gabungan" : "Add Income"))}
            </Button>
          </div>
        </form>

        <LedgerEntriesTable
          entries={entries}
          project={project}
          retType={retType}
          voAggregation={voAgg}
          accounts={accounts}
          readOnly={readOnly}
          editing={editing}
          setEditing={setEditing}
          startEdit={startEdit}
          cancelEdit={cancelEdit}
          saveEdit={saveEdit}
          askDel={askDel}
        />
      </DialogContent>
      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel({ ...confirmDel, open: o })}
        title="Delete this income entry?"
        description="The amount will be removed from Total Income for this project."
        confirmLabel="Delete"
        onConfirm={() => {
          const id = confirmDel.id;
          setConfirmDel({ open: false, id: null });
          if (id) del(id);
        }}
        testid="confirm-del-income"
      />
    </Dialog>
  );
}
